<script>
import IncidentForm from "@/components/Incident/IncidentForm.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column } from "@/components/shared/PaginatedTableColumns";
import { isAdminOfLoanable } from "@/helpers/permissions/loanables";
import Vue from "vue";

export default Vue.extend({
  name: "IncidentTable",
  components: {
    IncidentForm,
    PaginatedTable,
  },
  props: {
    loanableId: {
      type: String,
      default: null,
    },
    libraryId: {
      type: String,
      default: null,
    },
    initialFilters: {
      type: Object,
      default: () => ({}),
    },
    blockingOnly: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    let fetchParams = {};
    if (this.loanableId) {
      fetchParams.loanable_id = this.loanableId;
    } else if (this.libraryId) {
      fetchParams["loanable.library_id"] = this.libraryId;
    } else {
      fetchParams.for = "edit";
    }
    if (this.blockingOnly) {
      fetchParams.is_blocking = true;
    }
    return {
      fetchParams,
    };
  },
  computed: {
    columns() {
      let columns = [
        new Column("id", this.$t("lists.id"), "id"),

        Column.withSelect("status", this.$t("incidents.fields.status.title"), [
          { value: null, label: this.$t("incidents.fields.status.options.null.all") },
          {
            value: "in_process",
            label: this.$t("incidents.fields.status.options.in_process"),
            variant: "warning",
          },
          {
            value: "completed",
            label: this.$t("incidents.fields.status.options.completed"),
            variant: "success",
          },
        ]),

        Column.withSelect(
          "incident_type",
          this.$t("incidents.fields.type.title"),
          [
            { value: null, label: this.$t("incidents.fields.type.options.null.all") },
            {
              value: "accident",
              label: this.$t("incidents.fields.type.options.accident"),
              variant: "danger",
            },
            {
              value: "small_incident",
              label: this.$t("incidents.fields.type.options.small_incident"),
              variant: "warning",
            },
            {
              value: "general",
              label: this.$t("incidents.fields.type.options.general"),
              variant: "secondary",
            },
          ],
          { showByDefault: false }
        ),

        new Column("blocking_until", this.$t("incidents.fields.blocking_until"), "date", {
          showByDefault: false,
        }),
        new Column("created_at", this.$t("incidents.fields.created_at"), "date", {
          showByDefault: false,
        }),
        new Column("updated_at", this.$t("fields.updated_at"), "date"),
      ];

      if (!this.loanableId) {
        columns.splice(
          1,
          0,
          Column.withRelation(
            "loanable.name",
            this.$t("incidents.fields.loanable_name"),
            { relation: "loanables", label: "name", field: "id", params: { for: "admin" } },
            "loanable_id",
            { urlFct: (incident) => `/loanables/${incident.loanable.id}` }
          )
        );
      }

      if (!this.hideDetails) {
        columns.splice(
          3,
          0,
          Column.withRelation(
            "assignee.full_name",
            this.$t("incidents.fields.assignee"),
            { relation: "users", label: "full_name", field: "id", params: { for: "admin" } },
            "assignee_id",
            {
              urlFct: (incident) =>
                this.isAdmin(incident.loanable) ? `/admin/users/${incident.assignee.id}` : null,
              showByDefault: false,
            }
          ),
          new Column("loan_id", this.$t("incidents.fields.source"), "id", {
            urlFct: (incident) =>
              this.isAdmin(incident.loanable)
                ? `/admin/loans/${incident.loan_id}`
                : `/loans/${incident.loan_id}`,
            formatter: (id) => (id ? this.$t("incidents.incident_table.loan_number", { id }) : ""),
          }),
          Column.withRelation(
            "reported_by_user.full_name",
            this.$t("incidents.fields.reported_by"),
            { relation: "users", label: "full_name", field: "id", params: { for: "admin" } },
            "reported_by_user_id",
            {
              urlFct: (incident) =>
                this.isAdmin(incident.loanable)
                  ? `/admin/users/${incident.reported_by_user.id}`
                  : null,
              showByDefault: false,
            }
          ),
          Column.withRelation(
            "resolved_by_user.full_name",
            this.$t("incidents.fields.resolved_by"),
            { relation: "users", label: "full_name", field: "id", params: { for: "admin" } },
            "resolved_by_user_id",
            {
              urlFct: (incident) =>
                this.isAdmin(incident.loanable)
                  ? `/admin/users/${incident.resolved_by_user.id}`
                  : null,
              showByDefault: false,
            }
          )
        );
      }

      return columns;
    },
  },
  methods: {
    isAdmin(loanable) {
      return isAdminOfLoanable(this.$store.state.user, loanable);
    },
  },
});
</script>

<template>
  <div>
    <paginated-table
      ref="incidentsTable"
      class="mt-4"
      :label="$tc('incidents.model_name', 2)"
      :fetch-params="fetchParams"
      endpoint="incidents"
      :columns="columns"
      :load-relations="[
        'loanable',
        'notes.author.avatar',
        'assignee.avatar',
        'reported_by_user.avatar',
        'resolved_by_user.avatar',
      ]"
      :initial-filters="initialFilters"
      :has-row-details="!hideDetails"
      editable-items
      :show-action-column="!hideDetails"
      :show-generate-csv="false"
      v-bind="$attrs"
    >
      <template #row-details="{ item, updateItem }">
        <div v-if="item.details_hidden" class="font-italic">
          {{ $t("incidents.incident_table.no_access") }}
        </div>
        <incident-form v-else :incident="item" :loanable="item.loanable" @update="updateItem" />
      </template>
      <template #head-buttons>
        <slot name="head-buttons" />
      </template>
    </paginated-table>
  </div>
</template>

<style scoped lang="scss"></style>
